import React, {createContext, ReactElement, useContext, useEffect, useState} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {
  GetProviderProfileRequest,
  GetProviderProfileResponse,
  IApiBusinessProfileProto,
  IApiBusinessProto,
  IApiProviderProto,
  IProviderClientSettings,
} from "./provider_api";
import {useRpcSender} from "./util/RpcSender";
import {useImpersonation} from "./App/AdminHeader/AdminUtils";
import {RpcState} from "./util/RpcState";
import {useKensuAuth} from "./util/authwrapper/AuthWrapper";


/**
 * This context contains information about the provider (logged-in-user) and
 * the business. It's possible that either is null, or impersonated if the user
 * is an admin.
 *
 * Setters are provided so users of this context can update the profile,
 * typically after doing a mutation via the server.
 */
export const ProviderProfileContext = createContext<{

  authenticatedUserRoles: string[],
  
  // NOTE: the provider and business may be impersonated if the logged in user is
  // an admin and they've chosen a different provider in the AdminHeader.
  provider: IApiProviderProto | null | undefined,
  setProvider: (user: IApiProviderProto | null | undefined) => void;

  profileLoading: boolean,
  profileError: boolean,

  // There is only one business profile now; versioning died a horrific death.
  businessProfile: IApiBusinessProfileProto | null | undefined,
  setBusinessProfile: (profile: IApiBusinessProfileProto | null | undefined) => void;
  
  business: IApiBusinessProto | null | undefined,
  setBusiness: (business: IApiBusinessProto | null | undefined) => void,
  
  profileRpcState: RpcState,
  forceReload: () => void,

  settings: IProviderClientSettings,
  
}>({
  authenticatedUserRoles: [],
  provider: null,
  profileLoading: false,
  profileError: false,
  setProvider: () => {},
  businessProfile: null,
  setBusinessProfile: () => {},
  business: null,
  setBusiness: () => {},
  forceReload: () => {},
  profileRpcState: {state: 'pending'},
  settings: {}
});

export const useProviderProfile = () => {
  return useContext(ProviderProfileContext)
}

interface Props {
  children?: ReactElement
}

/**
 * Provides information both about the logged-in user (aka the PROVIDER)
 * and the business. This is loaded from the get_provider_profile action.
 *
 * @return {JSX.Element}
 * @constructor
 */
export const ProviderProfileProvider = (props: Props) => {

  const {user} = useKensuAuth();
  const rpcSender = useRpcSender();
  const impersonation = useImpersonation()

  const [authenticatedUserRoles, setAuthenticatedUserRoles] = useState<string[]>([]);
  const [provider, setProvider] = useState<IApiProviderProto | null | undefined>();
  const [business, setBusiness] = useState<IApiBusinessProto | null | undefined>();
  const [businessProfile, setBusinessProfile] = useState<IApiBusinessProfileProto | null | undefined>();
  
  // This forces a reload of everything - the first use case I found is when accepting a login
  // invite for a user, there's a lot of stuff to send down the wire - business profile, live profile,
  // the updated provider. Rather than jam all of that in the RPC response for redeeming an invite,
  // I set it to force a reload here.
  const [reloadIndex, setReloadIndex] = useState<number>(0);

  const [profileRpcState, setProfileRpcState] = useState<RpcState>({ state: 'pending'})
  
  const [settings, setSettings] = useState<IProviderClientSettings>({});
  
  useEffect(() => {

    if (!user) {
      setAuthenticatedUserRoles([]);
      setBusinessProfile(undefined);
      setProvider(undefined);
      setProfileRpcState({state: 'none'});
      return;
    }

    // Download it immediately at the beginning...
    console.log('[Fetching provider profile...]');
    setAuthenticatedUserRoles([]);
    setBusinessProfile(undefined);
    setProvider(undefined)
    setProfileRpcState({state: 'pending'});

    rpcSender.send("get_provider_profile",
        GetProviderProfileRequest.encode(new GetProviderProfileRequest()).finish())
        .then(response => {
          let profileResponse = GetProviderProfileResponse.decode(response);
          setAuthenticatedUserRoles(profileResponse.authenticatedUserRoles);
          setProvider(profileResponse.provider);
          setBusinessProfile(profileResponse.businessProfile);
          setBusiness(profileResponse.business);
          setSettings(profileResponse.clientSettings ?? {})
          setProfileRpcState({state: 'success'});
        })
        .catch(error => {
          setProfileRpcState({state: 'error', message: `Error loading profile (${error})`});
        });

  }, [user, impersonation, reloadIndex]);

  return (<ProviderProfileContext.Provider value={{
    authenticatedUserRoles: authenticatedUserRoles,
    provider: provider,
    setProvider: setProvider,
    profileLoading: profileRpcState.state == 'pending',
    profileError: profileRpcState.state == 'error',
    businessProfile: businessProfile,
    setBusinessProfile: setBusinessProfile,
    business: business,
    setBusiness: setBusiness,
    forceReload: () => setReloadIndex(i => i + 1),
    profileRpcState: profileRpcState,
    settings: settings,
  }}>
    {props.children}
  </ProviderProfileContext.Provider>);
};
